import type { Cell, CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { FieldPath } from 'react-hook-form';
import { type FieldArrayWithId, type UseFormReturn } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { createValidFromGlyph } from 'af-root/scenes/Company/Jobs/Preview/Materials/JobMaterialsTable/JobMaterialsTableForm/createValidFromGlyph';

import type { W_MaterialGroup_FindForCompany_VM_Single } from 'ab-api/web/materialGroup/findForCompany';
import type { W_Vendor_FindAllForCompany_VM_single } from 'ab-api/web/vendor/findAllForCompany';

import type { QuantityUnitType, PlainUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { ConstrainedUnitEnum, PlainUnitList, QuantityUnitLabel } from '@acceligentllc/shared/enums/quantityUnit';

import { numberFormatter } from 'af-utils/format.util';

import SelectAll from 'af-components/Controls/SelectAll';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { DisplayCell } from 'af-components/RHFTableCellComponents/Input/DisplayCell';
import type { InputCellType } from 'af-components/RHFTableCellComponents/Input/types';
import type { SimpleTableProps } from 'af-components/RHFTableCellComponents/SimpleTable/types';
import DropdownCellRenderer from 'af-components/RHFTableCellRenderers/DropdownCellRenderer';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import * as MaterialActions from 'af-actions/material';
import * as MaterialGroupActions from 'af-actions/materialGroup';
import * as VendorActions from 'af-actions/vendor';

import BaseCostInput from './BaseCostInput';
import CheckboxCell from './CheckboxCell';
import type { MaterialEditFM, MaterialFM, MaterialsFormInput, MaterialsFormOutput } from './formModel';
import { MaterialFMBaseCostHistorySchema, MaterialFMBaseSchema } from './formModel';
import MaterialRateHistoryButton from './MaterialRateHistoryButton';
import RateInput from './RateInput';
import SelectionCell from './SelectionCell';
import styles from './styles.module.scss';
import type { DeleteAndUndoCellProps } from '../../../../../Shared/Materials/DeleteAndUndoCell';
import DeleteAndUndoCell from '../../../../../Shared/Materials/DeleteAndUndoCell';

export * from './formModel';

interface OwnProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form: UseFormReturn<MaterialsFormInput, any, MaterialsFormOutput>;
	readonly: boolean;
	fields: FieldArrayWithId<MaterialsFormInput, 'materials', 'id'>[];
	selectedMaterials: Record<string, true>;
	onMaterialCheck: (id: number) => void;
	onRatePreview: (material: MaterialEditFM) => void;
	onDelete: (index: number) => void;
	removeFutureCost: (materialId: number) => void;
	selectAllMaterials: () => void;
	clearSelectedMaterials: () => void;
	selectedMaterialCount: number;
	totalMaterialCount: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

interface MaterialFMGroup {
	name: string;
}

type UnitOptionType = PlainUnitEnum | ConstrainedUnitEnum.PCT;

const unitOptionsMap = [...PlainUnitList, ConstrainedUnitEnum.PCT].reduce<Record<string, DropdownOption<UnitOptionType>>>((_acc, _unit) => {
	_acc[_unit] = { id: _unit, label: QuantityUnitLabel[_unit] };
	return _acc;
}, {});

// ensures uniqe units, there's a thing on shared where PlainUnitList has 2 'LFT' entries
const unitOptions = Object.values(unitOptionsMap);

function isExistingMaterial(material: MaterialFM): material is MaterialEditFM {
	return material.type === 'existing';
}

type MaterialColumn = keyof MaterialFM;

type MaterialRate = 'shippingMarkup' | 'salesMarkup' | 'tax';

export type MaterialFieldPath = FieldPath<MaterialsFormInput>;

type MaterialCell = MaterialFM & SimpleTableRow;

interface DropdownOption<T> {
	id: T;
	label: string;
}

const MaterialsTableForm: React.FC<Props> = (props) => {
	const {
		checkVendorUniqueId,
		fields,
		readonly,
		findCompanyMaterialGroups,
		form,
		findAllVendorsForCompany,
		onRatePreview,
		onDelete,
		selectedMaterials,
		onMaterialCheck,
		removeFutureCost,
		selectAllMaterials,
		clearSelectedMaterials,
		selectedMaterialCount,
		totalMaterialCount,
	} = props;

	const [materialGroups, setMaterialGroups] = React.useState<Nullable<W_MaterialGroup_FindForCompany_VM_Single[]>>(null);
	const [vendors, setVendors] = React.useState<Nullable<W_Vendor_FindAllForCompany_VM_single[]>>(null);

	const loadMaterialGroups = React.useCallback(async () => {
		const groups = await findCompanyMaterialGroups();
		setMaterialGroups(groups.materialGroups);
	}, [findCompanyMaterialGroups]);

	const loadVendors = React.useCallback(async () => {
		const res = await findAllVendorsForCompany();
		setVendors(res.vendors);
	}, [findAllVendorsForCompany]);

	React.useEffect(() => {
		loadMaterialGroups();
	}, [loadMaterialGroups]);

	React.useEffect(() => {
		loadVendors();
	}, [loadVendors]);

	const {
		control,
		setError,
		clearErrors,
		getValues,
		unregister,
		trigger,
	} = form;

	const isVendorUniqueIdAvailable = React.useCallback(async (data: Pick<MaterialFM, 'vendorId' | 'vendorUniqueId' | 'type' | 'materialId'>) => {
		const { vendorId, vendorUniqueId, type } = data;

		if (!vendorId || !vendorUniqueId) {
			return true;
		}

		const materialInDatabase = await checkVendorUniqueId({ vendorId, uniqueId: vendorUniqueId });
		if (!materialInDatabase) {
			return true;
		}

		if (type === 'existing') {
			return materialInDatabase.id === null || data.materialId === materialInDatabase.id;
		}
		return materialInDatabase.id === null;
	}, [checkVendorUniqueId]);

	const validateField = React.useCallback(async (cellName: MaterialFieldPath) => {
		const value = getValues(cellName);
		const [materialsItemFieldName, indexFieldName, materialFieldName, rateFieldName] = cellName.split('.');
		if (['vendorId', 'vendorUniqueId'].includes(materialFieldName)) {
			const PartialSchema = MaterialFMBaseSchema
				.pick({ vendorId: true, vendorUniqueId: true, type: true, materialId: true })
				.superRefine(async (data, context) => {
					if (!data.vendorId || !data.vendorUniqueId) {
						return;
					}
					const isAvailable = await isVendorUniqueIdAvailable(data);
					if (!isAvailable) {
						context.addIssue({
							code: 'custom',
							message: `Duplicate Vendor ID: ${data.vendorUniqueId}`,
							path: ['vendorUniqueId'],
						});
						context.addIssue({
							code: 'custom',
							message: `Duplicate Vendor ID: ${data.vendorUniqueId}`,
							path: ['vendorId'],
						});
					}
				});
			const materialKey = `${materialsItemFieldName}.${indexFieldName}` as `materials.${number}`;
			const material = getValues(materialKey);
			const { error } = await PartialSchema.safeParseAsync(material);
			if (error) {
				setError(`${materialKey}.vendorId`, { message: error.message });
				setError(`${materialKey}.vendorUniqueId`, { message: error.message });
			} else {
				clearErrors(`${materialKey}.vendorId`);
				clearErrors(`${materialKey}.vendorUniqueId`);
			}
			await trigger([`${materialKey}.vendorId`, `${materialKey}.vendorUniqueId`]);
			return;
		}
		if (['currentRate', 'futureRate'].includes(materialFieldName)) {
			const PartialSchema = MaterialFMBaseCostHistorySchema.pick({ [rateFieldName]: true });
			const { error } = PartialSchema.safeParse({ [rateFieldName]: value });
			if (error) {
				setError(cellName, { message: error.message });
			} else {
				clearErrors(cellName);
			}

			await trigger(cellName);
			return;
		}
		const PartialSchema = MaterialFMBaseSchema.pick({ [materialFieldName]: true });
		const { error } = PartialSchema.safeParse({ [materialFieldName]: value });

		if (error) {
			setError(cellName, { message: error.message });
		} else {
			clearErrors(cellName);
		}

		await trigger(cellName);
	}, [clearErrors, getValues, isVendorUniqueIdAvailable, setError, trigger]);

	const renderSelectionCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const { type, materialId } = cell.row.original;
		if (type === 'new' || !readonly) {
			return null;
		}
		const isChecked = !!selectedMaterials[materialId];

		return (
			<SelectionCell
				id={materialId}
				isChecked={isChecked}
				onChange={onMaterialCheck}
			/>
		);
	}, [onMaterialCheck, readonly, selectedMaterials]);

	const renderInputCell = React.useCallback((
		cellName: MaterialColumn,
		cellType: InputCellType
	) => (cell: CellContext<MaterialCell, string>) => {
		return (
			<InputCellRenderer
				control={control}
				defaultValue={cell.getValue() ?? '-'}
				fieldsLength={fields.length}
				getValues={getValues}
				name={`materials.${cell.row.original.index}.${cellName}`}
				onChange={validateField}
				readonly={readonly}
				rhfId={cell.row.original.id}
				type={cellType}
				unregister={unregister}
			/>
		);
	}, [control, fields.length, getValues, readonly, unregister, validateField]);

	const renderBaseCostCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const { original: material } = cell.row;
		const isExisting = isExistingMaterial(material);
		const { index, currentRate, id } = material;

		const futureRate = isExisting ? material.futureRate : null;

		const resolvedFutureRateCellLineClassName = `${styles.cellLine} ${!readonly ? styles.inEdit : ''}`;

		return (
			<div className={styles.cell}>
				<BaseCostInput
					containerClassName={resolvedFutureRateCellLineClassName}
					control={control}
					defaultValue={currentRate.baseCost}
					displayOnly={!!futureRate}
					fieldsLength={fields.length}
					getValues={getValues}
					name={`materials.${index}.currentRate.baseCost`}
					onChange={validateField}
					readonly={readonly}
					rhfId={id}
					unregister={unregister}
				/>
				{futureRate && (
					<BaseCostInput
						containerClassName={resolvedFutureRateCellLineClassName}
						control={control}
						defaultValue={futureRate.baseCost}
						displayOnly={false}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`materials.${index}.futureRate.baseCost`}
						onChange={validateField}
						readonly={readonly}
						readonlyFieldClassName={readonly ? styles.inactiveReadonlyField : undefined}
						rhfId={cell.row.original.id}
						unregister={unregister}
						validFrom={futureRate.validFrom}
					/>
				)}
			</div>
		);
	}, [control, fields.length, getValues, readonly, unregister, validateField]);

	const renderTotalCostCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		const { original: material } = cell.row;
		const isExisting = isExistingMaterial(material);
		const { currentRate: { totalCost } } = cell.row.original;

		const futureRate = isExisting ? material.futureRate : null;

		return (
			<div className={styles.cell}>
				<div className={styles.cellLine}>
					<DisplayCell
						elementKey="totalCost"
						name="totalCost"
						readonly={true}
						type="dollar"
						value={numberFormatter(2, 2).format(totalCost)}
					/>
				</div>
				{!!futureRate && (
					<div className={styles.cellLine}>
						<DisplayCell
							className={styles.inactiveReadonlyField}
							elementKey="totalCost"
							name="totalCost"
							prefixGlyphs={[createValidFromGlyph(futureRate.validFrom)]}
							readonly={true}
							type="dollar"
							value={numberFormatter(2, 2).format(futureRate.totalCost)}
						/>
					</div>
				)}
			</div>
		);
	}, []);

	const renderRateCell = React.useCallback((
		cellName: MaterialRate
	) => (cell: CellContext<MaterialCell, string>) => {
		const { original: material } = cell.row;
		const isExisting = isExistingMaterial(material);
		const { index, currentRate, id } = material;

		const futureRate = isExisting ? material.futureRate : null;

		const resolvedCurrentRateCellLineClassName = `${styles.cellLine} ${(!readonly && !futureRate) ? styles.inEdit : ''}`;
		const resolvedFutureRateCellLineClassName = `${styles.cellLine} ${!readonly ? styles.inEdit : ''}`;

		return (
			<div className={styles.cell}>
				<RateInput
					containerClassName={resolvedCurrentRateCellLineClassName}
					control={control}
					displayOnly={!!futureRate}
					fieldsLength={fields.length}
					getValues={getValues}
					name={`materials.${index}.currentRate.${cellName}`}
					onChange={validateField}
					rate={cellName}
					rateData={currentRate}
					readonly={readonly}
					rhfId={id}
					unregister={unregister}
				/>
				{futureRate && (
					<RateInput
						containerClassName={resolvedFutureRateCellLineClassName}
						control={control}
						displayOnly={false}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`materials.${index}.futureRate.${cellName}`}
						onChange={validateField}
						rate={cellName}
						rateData={futureRate}
						readonly={readonly}
						readonlyFieldClassName={readonly ? styles.inactiveReadonlyField : undefined}
						rhfId={cell.row.original.id}
						unregister={unregister}
						validFrom={futureRate.validFrom}
					/>
				)}
			</div>
		);
	}, [control, fields.length, getValues, readonly, unregister, validateField]);

	const filterGroups = React.useCallback((option: MaterialFMGroup, searchText: string) => {
		return option.name.toLowerCase().includes(searchText.toLowerCase());
	}, []);

	const renderGroupCell = React.useCallback((cellName: MaterialColumn) => (cell: CellContext<MaterialCell, string>) => {
		const name = `materials.${cell.row.original.index}.${cellName}` as const;

		const placeholder = getValues(name)?.toString() ?? null;

		return (
			<DropdownCellRenderer
				blankOption="None"
				bold={true}
				control={control}
				fieldsLength={fields.length}
				filterable={true}
				filterBy={filterGroups}
				getValues={getValues}
				hasBlankOption={true}
				id={cell.row.original.id.toString()}
				labelKey="name"
				name={name}
				onChange={validateField}
				options={materialGroups}
				placeholder={placeholder ?? '-'}
				readonly={readonly}
				rhfId={cell.row.original.id}
				unregister={unregister}
				valueKey="name"
				withCaret={!readonly}
			/>
		);
	}, [control, fields.length, filterGroups, getValues, materialGroups, readonly, unregister, validateField]);

	const filterUnits = React.useCallback((option: DropdownOption<QuantityUnitType>, searchText: string) => {
		return option.label.toLowerCase().includes(searchText);
	}, []);

	const renderUnitCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		return (
			<DropdownCellRenderer
				control={control}
				fieldsLength={fields.length}
				filterable={true}
				filterBy={filterUnits}
				getValues={getValues}
				id={cell.row.original.id.toString()}
				labelKey="label"
				name={`materials.${cell.row.original.index}.unit`}
				onChange={validateField}
				options={unitOptions}
				placeholder="Select Unit"
				readonly={readonly}
				rhfId={cell.row.original.id}
				unregister={unregister}
				valueKey="id"
				withCaret={!readonly}
			/>
		);
	}, [control, fields.length, filterUnits, getValues, readonly, unregister, validateField]);

	const renderItemNumber = React.useCallback((cell: CellContext<MaterialCell, number>) => {
		return (
			<DisplayCell
				elementKey="totalCost"
				name="totalCost"
				readonly={true}
				value={cell.getValue()}
			/>
		);
	}, []);

	const filterVendor = React.useCallback((vendor: W_Vendor_FindAllForCompany_VM_single) => {
		return !vendor.deletedAt;
	}, []);

	const searchVendor = React.useCallback((vendor: W_Vendor_FindAllForCompany_VM_single, search: string) => {
		return vendor.name.toLowerCase().includes(search.toLowerCase());
	}, []);

	const renderVendorCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		return (
			<DropdownCellRenderer
				control={control}
				fieldsLength={fields.length}
				filterable={true}
				filterBy={searchVendor}
				filterOptions={filterVendor}
				getValues={getValues}
				hasBlankOption={true}
				id={cell.row.original.id.toString()}
				labelKey="name"
				name={`materials.${cell.row.original.index}.vendorId`}
				onChange={validateField}
				options={vendors}
				placeholder="-"
				readonly={readonly}
				rhfId={`${cell.row.original.id}-vendorId`}
				unregister={unregister}
				valueKey="id"
				withCaret={!readonly}
			/>
		);
	}, [control, fields.length, filterVendor, getValues, readonly, searchVendor, unregister, validateField, vendors]);

	const renderDefaultOnJobCell = React.useCallback((cell: CellContext<MaterialCell, string>) => {
		return (
			<CheckboxCell
				control={control}
				disabled={readonly}
				name={`materials.${cell.row.original.index}.defaultOnJob`}
			/>
		);
	}, [control, readonly]);

	const renderRatePreviewCell = React.useCallback((cell: CellContext<MaterialCell, Nullable<number>>) => {
		if (!isExistingMaterial(cell.row.original)) {
			return null;
		}

		return (
			<MaterialRateHistoryButton material={cell.row.original} onRateHistory={onRatePreview} />
		);
	}, [onRatePreview]);

	const renderDeleteAndUndoTomorrowEditCell = React.useCallback((cell: CellContext<MaterialCell, number>) => {
		const { original: material } = cell.row;
		const { index } = cell.row.original;

		let deleteAndUndoCellProps: DeleteAndUndoCellProps = {};

		if (readonly && isExistingMaterial(material)) {
			const { materialId, futureRate } = material;

			if (!!futureRate) {
				deleteAndUndoCellProps = {
					...deleteAndUndoCellProps,
					withResetChanges: true,
					materialId,
					onResetChanges: removeFutureCost,
				};
			}
		}

		if (!readonly) {
			deleteAndUndoCellProps = {
				...deleteAndUndoCellProps,
				withDelete: true,
				index,
				onDelete,
			};
		}

		return (
			<DeleteAndUndoCell {...deleteAndUndoCellProps} />
		);
	}, [onDelete, readonly, removeFutureCost]);

	const renderSelectAllHeader = React.useCallback(() => {
		return (
			<SelectAll
				onClear={clearSelectedMaterials}
				onSelectAll={selectAllMaterials}
				selectedCount={selectedMaterialCount}
				totalCount={totalMaterialCount}
			/>
		);
	}, [clearSelectedMaterials, selectAllMaterials, selectedMaterialCount, totalMaterialCount]);

	const columns = React.useMemo<SimpleTableProps<MaterialCell>['columns']>(() => {
		return [
			{
				id: 'selected',
				isDisplayColumn: true,
				header: renderSelectAllHeader,
				cell: renderSelectionCell,
				size: 40,
			},
			{
				id: 'group1',
				accessor: 'group1',
				header: 'Group 1 *',
				cell: renderGroupCell('group1'),
			},
			{
				id: 'group2',
				accessor: 'group2',
				header: 'Group 2',
				cell: renderGroupCell('group2'),
			},
			{
				id: 'group3',
				accessor: 'group3',
				header: 'Group 3',
				cell: renderGroupCell('group3'),
			},
			{
				id: 'vendorId',
				accessor: 'vendorId',
				header: 'Vendor',
				cell: renderVendorCell,
			},
			{
				id: 'vendorUniqueId',
				accessor: 'vendorUniqueId',
				header: 'Vendor ID',
				cell: renderInputCell('vendorUniqueId', 'text'),
			},
			{
				id: 'tags',
				accessor: 'tags',
				header: 'Tags',
				cell: renderInputCell('tags', 'text'),
			},
			{
				id: 'itemNumber',
				accessor: 'itemNumber',
				header: 'Item Number',
				cell: renderItemNumber,
				size: 90,
			},
			{
				id: 'displayName',
				accessor: 'displayName',
				header: 'Display Name *',
				cell: renderInputCell('displayName', 'text'),
				size: 160,
			},
			{
				id: 'description',
				accessor: 'description',
				header: 'Description',
				cell: renderInputCell('description', 'text'),
				size: 160,
			},
			{
				id: 'unit',
				accessor: 'unit',
				header: 'Unit *',
				cell: renderUnitCell,
			},
			{
				id: 'baseCost',
				accessor: 'baseCost',
				header: 'Base Cost *',
				cell: renderBaseCostCell,
				externalClickControl: true,
			},
			{
				id: 'shippingMarkup',
				accessor: 'shippingMarkup',
				header: 'Shipping Markup',
				cell: renderRateCell('shippingMarkup'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'salesMarkup',
				accessor: 'salesMarkup',
				header: 'Sales Markup',
				cell: renderRateCell('salesMarkup'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'tax',
				accessor: 'tax',
				header: 'Tax',
				cell: renderRateCell('tax'),
				size: 130,
				externalClickControl: true,
			},
			{
				id: 'totalCost',
				accessor: 'totalCost',
				header: 'Total Cost',
				cell: renderTotalCostCell,
			},
			{
				id: 'defaultOnJob',
				accessor: 'defaultOnJob',
				header: 'Default on Job',
				cell: renderDefaultOnJobCell,
				size: 100,
			},
			{
				id: 'ratePreview',
				accessor: 'materialId',
				header: '',
				cell: renderRatePreviewCell,
				size: 100,
			},
			{
				id: 'deleteAndUndo',
				accessor: 'id',
				header: '',
				cell: renderDeleteAndUndoTomorrowEditCell,
				size: 40,
			},
		];
	}, [
		renderSelectionCell,
		renderGroupCell,
		renderInputCell,
		renderItemNumber,
		renderRateCell,
		renderRatePreviewCell,
		renderTotalCostCell,
		renderUnitCell,
		renderVendorCell,
		renderDefaultOnJobCell,
		renderBaseCostCell,
		renderDeleteAndUndoTomorrowEditCell,
		renderSelectAllHeader,
	]);

	const getRowId = React.useCallback((row: MaterialCell) => {
		return row.id;
	}, []);

	const getCellClassName = React.useCallback((cell: Cell<MaterialCell, unknown>) => {
		if (cell.column.id === 'selected') {
			return styles.selectCell;
		}
		return '';
	}, []);

	return (
		<SimpleTableRHFWrapper
			className={styles.MaterialsTable}
			columns={columns}
			fields={fields}
			formAccessorName="materials"
			getCellClassName={getCellClassName}
			getRowId={getRowId}
			readonly={readonly}
		/>
	);
};

const mapDispatchToProps = () => {
	return {
		checkVendorUniqueId: MaterialActions.checkVendorUniqueId,
		findCompanyMaterialGroups: MaterialGroupActions.findCompanyMaterialGroups,
		findAllVendorsForCompany: VendorActions.findAllForCompany,
	};
};

const connector = connect(null, mapDispatchToProps());

export default connector(MaterialsTableForm);
